import {
  type BriqpayCartItem,
  type BriqpaySession,
  type BriqpayStandardCartItem,
  type CreateUpdateOrderDTO,
  CustomerType,
  type OrderDTO
} from 'ecosystem'
import { convertMinorUnitToValue, roundNumber, sumArrayOfNumbers } from 'shared-utils'
import { createError } from '../../../../utils/fetching'
import { generateBriqpayAddresses } from '../utils/briqpay'
import { type CompleteStorefrontOrderProps } from './types'
import {
  generateBasePayload,
  generatePayloadFromIngrid,
  generatePayloadFromNshift,
  generatePayloadFromPostNord
} from './utils'

const isStandardCartItem = (item: BriqpayCartItem): item is BriqpayStandardCartItem =>
  item.productType !== 'sales_tax' && item.productType !== 'gift_card'

const generateBasePayloadFromBriqpay = (
  oldOrder: OrderDTO,
  briqpaySession: BriqpaySession
): CreateUpdateOrderDTO => {
  const {
    billingAddress,
    shippingAddress,
    customerInfo: { email, firstName, lastName }
  } = generateBriqpayAddresses(briqpaySession, oldOrder)

  // Gift-card and sales-tax rows carry neither `totalAmount` nor `totalVatAmount`, so
  // every total below is summed from the standard rows only.
  const cart = (briqpaySession.data?.order?.cart ?? []).filter(isStandardCartItem)

  const shippingCost = convertMinorUnitToValue(
    sumArrayOfNumbers(
      cart
        .filter((item) => item.productType === 'shipping_fee')
        .map((item) => item.totalAmount ?? 0)
    )
  )

  // Briqpay expresses discounts per unit rather than as separate rows, but accepts a
  // negative `discount` row too — count both so a cart built elsewhere still reconciles.
  const orderDiscount = convertMinorUnitToValue(
    sumArrayOfNumbers(
      cart.map(
        (item) =>
          (item.unitDiscountAmountIncVat ?? 0) * item.quantity +
          (item.productType === 'discount' ? Math.abs(item.totalAmount ?? 0) : 0)
      )
    )
  )

  const orderTaxAmount = convertMinorUnitToValue(
    sumArrayOfNumbers(cart.map((item) => item.totalVatAmount ?? 0))
  )

  return {
    ...generateBasePayload(oldOrder),
    billingAddress,
    shippingAddress,
    currency: briqpaySession.data?.order?.currency ?? oldOrder.currency,
    customer: {
      email,
      firstName,
      lastName,
      // Fixed at session create and not changeable afterwards, so the session is
      // authoritative about which flow the shopper actually went through.
      type:
        briqpaySession.customerType === 'business' ? CustomerType.BUSINESS : CustomerType.CONSUMER
    },
    orderAmount: convertMinorUnitToValue(briqpaySession.data?.order?.amountIncVat ?? 0),
    orderTaxAmount: roundNumber(orderTaxAmount),
    orderDiscount: roundNumber(orderDiscount),
    shippingCost,

    // The Briqpay sessionId, already persisted at create time — restated here because
    // `completeStorefrontOrder` issues a full replace.
    paymentId: briqpaySession.sessionId,
    paymentProvider: 'BRIQPAY'
  }
}

export const briqpayPaymentProvider = ({
  order: oldOrder,
  paymentProviderInfo,
  shippingProviderInfo
}: CompleteStorefrontOrderProps): CreateUpdateOrderDTO => {
  if (!paymentProviderInfo.providerResponse?.briqpayResponse) {
    throw createError('Not found BRIQPAY session')
  }
  const shippingProviderName = shippingProviderInfo?.providerName ?? 'DEFAULT'

  let payload = generateBasePayloadFromBriqpay(
    oldOrder,
    paymentProviderInfo.providerResponse.briqpayResponse
  )

  switch (shippingProviderName) {
    case 'DEFAULT':
      break
    case 'POSTNORD':
      payload = generatePayloadFromPostNord(payload, shippingProviderInfo?.postnordResponse)
      break
    case 'INGRID':
      payload = generatePayloadFromIngrid(payload, shippingProviderInfo?.ingridResponse)
      break
    case 'NSHIFT':
      payload = generatePayloadFromNshift(payload, shippingProviderInfo?.nshiftResponse)
  }

  return payload
}
